import { useState, Fragment } from 'react';
import { Tab } from '@headlessui/react';
import { Dialog, Transition } from '@headlessui/react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';
import {
  VideoCameraIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  BuildingOfficeIcon,
  PencilSquareIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

interface EmergencyContact {
  name: string;
  role: string;
  callSign: string;
  contactNumber: string;
}

interface EmergencyContactsState {
  contacts: EmergencyContact[];
}

interface UpdateFormData {
  role: string;
  callSign: string;
  contactNumber: string;
}

interface LocationDetails {
  name: string;
  address: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
}

interface UpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  onUpdate: (data: any) => void;
  type: 'contacts' | 'documents';
}

function UpdateModal({ isOpen, onClose, title, onUpdate, type }: UpdateModalProps) {
  const [isUpdating, setIsUpdating] = useState(false);
  const { 
    register, 
    handleSubmit, 
    reset, 
    formState: { errors } 
  } = useForm<UpdateFormData>();

  const onSubmit: SubmitHandler<UpdateFormData> = async (data) => {
    setIsUpdating(true);
    try {
      // Validate data
      if (type === 'contacts' && (!data.role || !data.callSign || !data.contactNumber)) {
        throw new Error('All fields are required');
      }

      onUpdate(data);
      reset();
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                      {title}
                    </Dialog.Title>
                    <form onSubmit={handleSubmit(onSubmit)} className="mt-4">
                      {type === 'contacts' ? (
                        <div className="space-y-4">
                          <div>
                            <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                              Role
                            </label>
                            <div className="mt-1">
                              <input
                              type="text"
                              id="role"
                              {...register('role')}
                              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                              required
                              />
                            </div>
                          </div>
                          <div>
                            <label htmlFor="callSign" className="block text-sm font-medium text-gray-700">
                              Call-sign
                            </label>
                            <div className="mt-1">
                              <input
                              type="text"
                              id="callSign"
                              {...register('callSign')}
                              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                              required
                              />
                            </div>
                          </div>
                          <div>
                            <label htmlFor="contactNumber" className="block text-sm font-medium text-gray-700">
                              Contact Number
                            </label>
                            <div className="mt-1">
                              <input
                              type="tel"
                              id="contactNumber"
                              {...register('contactNumber')}
                              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                              required
                              />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div>
                            <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                              Document Title
                            </label>
                            <input
                              type="text"
                              id="title"
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                              required
                            />
                          </div>
                          <div>
                            <label htmlFor="link" className="block text-sm font-medium text-gray-700">
                              Document Link
                            </label>
                            <input
                              type="url"
                              id="link"
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                              required
                            />
                          </div>
                        </div>
                      )}
                      <div className="mt-5 sm:mt-6 sm:flex sm:flex-row-reverse">
                        <button
                          type="submit"
                          disabled={isUpdating}
                          className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isUpdating ? 'Updating...' : 'Update'}
                        </button>
                        <button
                          type="button"
                          className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                          onClick={onClose}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default function WarRoom() {
  const [emergencyContacts, setEmergencyContacts] = useState<EmergencyContactsState>({
    contacts: [
      { name: 'Lebron Jordan', role: 'Executive-in-Charge (EIC)', callSign: 'GOAT 2', contactNumber: '+1 (888) 777-4444' },
      { name: 'Michael James', role: 'Senior Executive-in-Charge (SEIC)', callSign: 'GOAT 1', contactNumber: '+1 (111) 111-1111' },
      { name: 'Larry Hawk', role: 'Lead Incident Handler (LIH)', callSign: 'Sniper', contactNumber: '+1 (333) 757-4444' },
      { name: 'Stephen Murry', role: 'CSIRT Team Lead', callSign: 'Overwatch', contactNumber: '+1 (415) 444-5555' }
    ]
  });

  const [quickAccessDocs, setQuickAccessDocs] = useState([
    { title: 'Response Playbook', link: '#' },
    { title: 'Communication Templates', link: '#' },
    { title: 'Key Contacts', link: '#' }
  ]);

  const [warRoomDetails, setWarRoomDetails] = useState({
    name: 'Crisis Response War Room',
    videoLink: 'https://meet.example.com/crisis-room',
    callInNumber: '+1 (555) 000-0000',
    callInPin: '123456',
    webLink: 'https://crisis.example.com/war-room',
  });
  
  const [updateModal, setUpdateModal] = useState<{
    isOpen: boolean;
    type: 'contacts' | 'documents';
    title: string;
  }>(() => ({
    isOpen: false,
    type: 'contacts',
    title: '',
  }));

  const [physicalLocations, setPhysicalLocations] = useState({
    primary: {
      name: 'Main War Room',
      address: '123 Corporate HQ, 10th Floor',
      contactName: 'John Smith',
      contactPhone: '+1 (555) 123-4567',
      contactEmail: 'john.smith@company.com',
    },
    backup: {
      name: 'Backup War Room',
      address: '456 Branch Office, 5th Floor',
      contactName: 'Jane Doe',
      contactPhone: '+1 (555) 234-5678',
      contactEmail: 'jane.doe@company.com',
    },
    disaster: {
      name: 'Disaster Recovery Site',
      address: '789 Remote Location',
      contactName: 'Mike Johnson',
      contactPhone: '+1 (555) 345-6789',
      contactEmail: 'mike.johnson@company.com',
    },
  });

  const handleInputChange = (field: string, value: string) => {
    try {
      if (!value?.trim()) {
        throw new Error(`${field} cannot be empty`);
      }

      setWarRoomDetails(prev => ({
        ...prev,
        [field]: value.trim()
      }));

      toast.success('War room details updated successfully');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update details');
    }
  };

  const handleUpdateContacts = async (data: EmergencyContact) => {
    try {
      // Add validation
      if (!data) throw new Error('No data provided');

      // Update contacts
      setEmergencyContacts(prev => ({
        contacts: [...prev.contacts, {
          role: data.role.trim(),
          callSign: data.callSign.trim(),
          contactNumber: data.contactNumber.trim()
        }]
      }));

      toast.success('Contact updated successfully');
      setUpdateModal(prev => ({ ...prev, isOpen: false }));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update contact');
    }
  };

  const handleUpdateDocuments = async (data: any) => {
    try {
      // Add validation
      if (!data?.title || !data?.link) {
        throw new Error('All fields are required');
      }

      // Update documents
      setQuickAccessDocs(prev => [...prev, {
        title: data.title.trim(),
        link: data.link.trim()
      }]);
      
      toast.success('Document updated successfully');
      setUpdateModal(prev => ({ ...prev, isOpen: false }));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update document');
    }
  };

  const handleLocationChange = (
    locationType: 'primary' | 'backup' | 'disaster',
    field: keyof LocationDetails,
    value: string,
    shouldValidate = true
  ) => {
    try {
      if (shouldValidate && !value?.trim()) {
        throw new Error(`${field} cannot be empty`);
      }

      setPhysicalLocations(prev => ({
        ...prev,
        [locationType]: {
          ...prev[locationType],
          [field]: value.trim()
        }
      }));

      toast.success('Location updated successfully');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update location');
    }
  };

  const LocationForm = ({ 
    type, 
    title, 
    data 
  }: { 
    type: 'primary' | 'backup' | 'disaster';
    title: string;
    data: LocationDetails;
  }) => (
    <div className="border rounded-lg p-4">
      <h4 className="font-medium text-lg mb-4">{title}</h4>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Room Name</label>
          <input
            type="text"
            value={data.name}
            onChange={(e) => handleLocationChange(type, 'name', e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Address</label>
          <input
            type="text"
            value={data.address}
            onChange={(e) => handleLocationChange(type, 'address', e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Contact Person</label>
          <input
            type="text"
            value={data.contactName}
            onChange={(e) => handleLocationChange(type, 'contactName', e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Contact Phone</label>
            <input
              type="tel"
              value={data.contactPhone}
              onChange={(e) => handleLocationChange(type, 'contactPhone', e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Contact Email</label>
            <input
              type="email"
              value={data.contactEmail}
              onChange={(e) => handleLocationChange(type, 'contactEmail', e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const [categories] = useState([
    {
      id: 'overview',
      name: 'Overview',
      icon: UserGroupIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-800">
              The war room acts as the command center during the cyber crisis where the crisis response team and stakeholders execute the crisis response plan and activities. It could be a physical and/or virtual meeting space and even physically distributed for redundancy purposes.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="border rounded-lg p-4">
              <h4 className="font-medium text-lg mb-2">Physical War Room</h4>
              <ul className="space-y-2 text-gray-600">
                <li>• Main Conference Room - 10th Floor</li>
                <li>• Backup Location - 5th Floor</li>
                <li>• Disaster Recovery Site - Remote Location</li>
              </ul>
            </div>
            <div className="border rounded-lg p-4">
              <h4 className="font-medium text-lg mb-2">Virtual War Room</h4>
              <ul className="space-y-2 text-gray-600">
                <li>• Primary Video Conference Link</li>
                <li>• Backup Communication Channels</li>
                <li>• Secure Document Sharing</li>
              </ul>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'physical-locations',
      name: 'Physical Locations',
      icon: BuildingOfficeIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-6">
            <LocationForm 
              type="primary" 
              title="Primary War Room" 
              data={physicalLocations.primary} 
            />
            <LocationForm 
              type="backup" 
              title="Backup War Room" 
              data={physicalLocations.backup} 
            />
            <LocationForm 
              type="disaster" 
              title="Disaster Recovery Site" 
              data={physicalLocations.disaster} 
            />
            <div className="flex justify-end">
              <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                Save All Locations
              </button>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'virtual-room',
      name: 'Virtual Room',
      icon: VideoCameraIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-6">
            <div className="border-b pb-6">
              <h3 className="text-lg font-medium mb-4">Virtual War Room Configuration</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="warRoomName" className="block text-sm font-medium text-gray-700">
                    War Room Name
                  </label>
                  <input
                    type="text"
                    id="warRoomName"
                    value={warRoomDetails.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="videoLink" className="block text-sm font-medium text-gray-700">
                    Video Conference Link
                  </label>
                  <input
                    type="text"
                    id="videoLink"
                    value={warRoomDetails.videoLink}
                    onChange={(e) => handleInputChange('videoLink', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="callInNumber" className="block text-sm font-medium text-gray-700">
                      Call-in Number
                    </label>
                    <input
                      type="text"
                      id="callInNumber"
                      value={warRoomDetails.callInNumber}
                      onChange={(e) => handleInputChange('callInNumber', e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="callInPin" className="block text-sm font-medium text-gray-700">
                      Call-in PIN
                    </label>
                    <input
                      type="text"
                      id="callInPin"
                      value={warRoomDetails.callInPin}
                      onChange={(e) => handleInputChange('callInPin', e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="webLink" className="block text-sm font-medium text-gray-700">
                    Web Access Link
                  </label>
                  <input
                    type="text"
                    id="webLink"
                    value={warRoomDetails.webLink}
                    onChange={(e) => handleInputChange('webLink', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>
                <div className="mt-4 flex space-x-3">
                  <button 
                    type="submit"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                    Save Configuration
                  </button>
                  <button className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                    Join Virtual Room
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'resources',
      name: 'Resources',
      icon: DocumentTextIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium">Emergency Contacts</h4>
                </div>
                <ul className="mt-2 space-y-3 text-sm text-gray-600">
                  {emergencyContacts.contacts.map((contact, index) => (
                    <li key={index}>
                      <div>
                        <span className="font-medium">{contact.name} - {contact.role}</span>
                        <div className="ml-4 text-gray-500">
                          <div>Call Sign: {contact.callSign}</div>
                          <div>Phone: {contact.contactNumber}</div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium">Quick Access Documents</h4>
                </div>
                <ul className="mt-2 space-y-2 text-sm text-gray-600">
                  <li>
                    <Link to="/response-plan" className="text-indigo-600 hover:text-indigo-900">
                      Response Playbook
                    </Link>
                  </li>
                  <li>
                    <Link to="/communications" className="text-indigo-600 hover:text-indigo-900">
                      Communication Templates
                    </Link>
                  </li>
                  <li>
                    <Link to="/organization" className="text-indigo-600 hover:text-indigo-900">
                      Key Contacts
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'status-updates',
      name: 'Status Updates',
      icon: ChatBubbleLeftRightIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-4">
              <div className="border rounded-lg p-4">
                <h4 className="font-medium">Status Updates</h4>
                <div className="mt-4 space-y-4">
                  <div className="bg-yellow-50 p-3 rounded-md">
                    <span className="text-sm font-medium text-yellow-800">Incident #2024-001</span>
                    <p className="text-sm text-yellow-600 mt-1">Investigation in progress</p>
                  </div>
                  <div className="bg-green-50 p-3 rounded-md">
                    <span className="text-sm font-medium text-green-800">Incident #2024-002</span>
                    <p className="text-sm text-green-600 mt-1">Containment phase completed</p>
                  </div>
                </div>
              </div>
              <div className="border rounded-lg p-4">
                <h4 className="font-medium">Team Chat</h4>
                <div className="mt-4">
                  <button className="w-full px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                    Open Secure Chat
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
  ]);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">War Room</h2>
      <Tab.Group>
        <Tab.List className="flex space-x-4 border-b border-gray-200">
          {categories.map((category) => (
            <Tab
              key={category.id}
              className={({ selected }) =>
                clsx(
                  'flex items-center space-x-2 py-2 px-4 text-sm font-medium border-b-2 outline-none',
                  selected
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                )
              }
            >
              <category.icon className="h-5 w-5" />
              <span>{category.name}</span>
            </Tab>
          ))}
        </Tab.List>
        <Tab.Panels>
          {categories.map((category) => (
            <Tab.Panel key={category.id}>{category.content}</Tab.Panel>
          ))}
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
}